'use client';

import { c as _c } from "react/compiler-runtime";
import { getStorage } from 'minimal-shared/utils';
import { union, clone, isEqual } from 'es-toolkit';
import { useState, Suspense, useEffect } from 'react';
import { useLocalStorage } from 'minimal-shared/hooks';
import { paths, newSearchParams } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { useTranslate } from 'src/locales';
import { getData, updateData } from 'src/lib/firebase';
import { toast } from 'src/components/snackbar';
import { SplashScreen } from 'src/components/loading-screen';
import { useAuthContext } from 'src/auth/hooks';
import { CheckoutContext } from './checkout-context';

// ----------------------------------------------------------------------

const CHECKOUT_STORAGE_KEY = 'me-checkout'; // 'mestore';
const CHECKOUT_STEPS = ['Cart', 'Shipping address', 'Payment'];
const initialState = {
  total: 0,
  items: [],
  subtotal: 0,
  discount: 0,
  shipping: 0,
  totalYape: 0,
  billing: null,
  totalItems: 0,
  typeOfSale: null,
  paymentCard: null,
  isFreeShipping: false,
  isCashOnDelivery: false,
  deliveryType: 'standard'
};
const normalizeStep = step => Math.min(Math.max(Number(step), 0), 2);
export function CheckoutProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SplashScreen />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Suspense fallback={t1}><CheckoutContainer>{children}</CheckoutContainer></Suspense>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function CheckoutContainer({
  children
}) {
  const router = useRouter();
  const {
    user
  } = useAuthContext();
  const searchParams = useSearchParams();
  const {
    t,
    currentLang
  } = useTranslate();
  const [loading, setLoading] = useState(true);
  const activeStep = normalizeStep(searchParams.get('step'));
  const {
    state,
    setState,
    resetState
  } = useLocalStorage(CHECKOUT_STORAGE_KEY, initialState, {
    initializeWithValue: false
  });
  const canReset = !isEqual(state, initialState);
  const completed = activeStep === CHECKOUT_STEPS.length;
  const onApplyDiscount = discount => setState({
    discount
  });
  const onApplyTotalYape = totalYape => setState({
    totalYape
  });
  const onApplyTypeOfSale = typeOfSale => setState({
    typeOfSale
  });
  const onApplyShipping = (shipping, deliveryType) => setState({
    shipping,
    deliveryType
  });
  const onApplyPaymentCard = newPaymentCard => {
    setState({
      paymentCard: newPaymentCard ? {
        ...state.paymentCard,
        ...newPaymentCard
      } : state.paymentCard
    });
  };
  const updateCheckout = items => {
    const discount = items.length ? state.discount : 0;
    const isCashOnDelivery = items.some(({
      cod
    }) => cod);
    const isFreeShipping = items.some(({
      shipping
    }) => shipping);
    const isDeliveryServiceSelect = state.deliveryType ?? state.typeOfSale;
    const totalItems = items.reduce((total, item) => total + item.quantity, 0);
    const subtotal = items.reduce((total, item) => total + item.quantity * item.priceSale, 0);
    setState({
      items,
      discount,
      subtotal,
      totalItems,
      isFreeShipping,
      isCashOnDelivery,
      total: subtotal + state.shipping - discount,
      billing: activeStep === 1 ? null : state.billing,
      typeOfSale: activeStep === 1 ? null : state.typeOfSale,
      paymentCard: activeStep === 2 ? state.paymentCard : null,
      deliveryType: activeStep === 1 ? 'standard' : state.deliveryType,
      shipping: isDeliveryServiceSelect && items.length && activeStep === 2 ? state.shipping : 0
    });
  };
  const onAddToCart = newItem => {
    if (newItem.country !== currentLang.countryName) {
      toast.error(t('The selected product is from another country'));
      return;
    }
    const items = state.items.map(item => item.id === newItem.id ? {
      ...item,
      quantity: item.quantity + newItem.quantity,
      size: union(item.size.flat(Infinity), newItem.size.flat(Infinity)),
      colors: union(item.colors.flat(Infinity), newItem.colors.flat(Infinity))
    } : item);
    if (!items.some(({
      id
    }) => id === newItem.id)) items.push(newItem);
    updateCheckout(items);
    toast.success(t('The product was added to the cart'), {
      position: 'top-left'
    });
  };
  const onChangeItemQuantity = (itemId, quantity) => {
    const items = state.items.map(item => item.id === itemId ? {
      ...item,
      quantity,
      size: item.size.slice(0, quantity),
      colors: item.colors.slice(0, quantity)
    } : item);
    updateCheckout(items);
  };
  const onRemoveFromCart = itemId => {
    const items = state.items.filter(({
      id
    }) => id !== itemId);
    updateCheckout(items);
  };
  const onResetCart = () => {
    if (completed) {
      resetState();
      router.push(`${paths.shop}${newSearchParams({
        country: currentLang.countryName
      })}`);
    }
  };
  const onCheckCartItems = async () => {
    const items = await Promise.all(clone(state.items).map(async ({
      id
    }) => getData({
      path: `promotions/${id}`,
      formatter: promotion => ({
        ...promotion,
        name: promotion.name,
        long: promotion.long,
        taxes: promotion.taxes,
        width: promotion.width,
        depth: promotion.depth,
        weight: promotion.weight,
        category: promotion.category,
        coverUrl: promotion.coverUrl,
        available: promotion.available,
        priceSale: promotion.priceSale
      })
    })).filter(({
      available
    }) => available > 0));
    const subtotal = items.reduce((total, item) => total + item.quantity * item.priceSale, 0);
    const total = subtotal + state.shipping - state.discount;
    setState({
      items,
      total
    });
  };
  const onChangeStep = (type, step) => {
    const targetStep = {
      go: step ?? 0,
      back: (activeStep ?? 0) - 1,
      next: (activeStep ?? 0) + 1
    }[type];
    router.push(`${paths.checkout}${newSearchParams({
      country: currentLang.countryName,
      step: targetStep
    })}`);
  };
  const onCreateAddress = address => {
    if (address.country === currentLang.countryName) {
      setState({
        billing: address
      });
      onChangeStep('next');
    } else {
      toast.error(t('The address must match the country of the selected products'));
    }
  };
  const onUpdateAddress = async address => {
    if (user?.id) {
      await updateData(`users/${user?.id}/addressBook/${address.id}`, {
        geocoder: address.geocoder
      });
    }
    setState({
      billing: address
    });
  };
  const initializeCheckout = () => {
    try {
      setLoading(true);
      const restoredValue = getStorage(CHECKOUT_STORAGE_KEY);
      if (restoredValue) updateCheckout(state.items);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    initializeCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const items = clone(state.items).filter(({
      country
    }) => country === currentLang.countryName);
    if (state.items.length !== items.length) updateCheckout(items);
    if (!items.length && activeStep > 0) {
      onChangeStep('go', 0);
    } else if (!state.billing && activeStep > 1) {
      onChangeStep('go', 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, state.billing, currentLang.countryName]);
  const contextValues = {
    state,
    setState,
    //
    activeStep,
    onChangeStep,
    steps: CHECKOUT_STEPS,
    //
    canReset,
    loading,
    completed,
    //
    onAddToCart,
    onResetCart,
    onCheckCartItems,
    onRemoveFromCart,
    onChangeItemQuantity,
    //
    onCreateAddress,
    onUpdateAddress,
    //
    onApplyDiscount,
    onApplyShipping,
    onApplyTotalYape,
    onApplyTypeOfSale,
    onApplyPaymentCard
  };
  return <CheckoutContext value={contextValues}>{children}</CheckoutContext>;
}